import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { forgotPassword, loginUser } from "../Api";
import { showSwal } from "../Helper/ShowAlert";
import AnimatedLoader from "../motionKit/AnimatedLoader";
import AmbientBackdrop from "../motionKit/AmbientBackdrop";
import FkAmbientCss from "../motionKit/FkAmbientCss";
import { loginSuccess } from "../redux/action";
import { useDispatch } from "react-redux";
import { isValidEmail } from "../Helper/Validations";
import { BetterThrottle } from "./Admin/Components/SearchFilterDropdown";
import { motion } from "framer-motion";
import GoogleIcon from "../assets/icons/google.svg";
import { Mail, Lock, Eye, EyeOff, Send, Check, ShieldCheck, Plane, WandSparkles, ArrowRight, FileCheck2, Shield } from "lucide-react";


const Login = () => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [showForgotScreen, setShowForgotScreen] = useState(true);
  const [emailError, setEmailError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const BtnRef = useRef(null);
  const [form, setForm] = useState({
    email: "",
    password: "",
    user: "role",
    forgotEmail: "",
  });

  const navigate = useNavigate();

  const login = useGoogleLogin({
    onSuccess: (codeResponse) => setUser(codeResponse),
    onError: (error) => console.log("Login Failed:", error),
  });

  //Logging in with google
  useEffect(() => {
    if (user) {
      setIsLoading(true);
      axios
        .get(
          `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${user.access_token}`,
          {
            headers: {
              Authorization: `Bearer ${user.access_token}`,
              Accept: "application/json",
            },
          }
        )
        .then(async (res) => {
          try {
            let response = await loginUser({
              email: res.data.email.toLowerCase(),
              id: res.data.id,
              way: "google",
              hd: "gmail.com",
            });
            dispatch(
              loginSuccess({ ...response.data.user }, response.data.token)
            );
            setIsLoading(false);
            // Show toast but navigate immediately for better UX
            showSwal("Success", response.data.message, 200);
            navigate(
              `/${response.data.user.role === "superAdmin"
                ? "admin"
                : response.data.user.role
              }/${response.data.user.role === "admin" || response.data.user.role === "superAdmin" ? "users" : response.data.user.role === "operator" ? "dashboard" : "freight-form"}`
            );
          } catch (error) {
            console.log(error);
            setIsLoading(false);
            const msg =
              error.response?.data?.error ||
              error.message ||
              "Network error — is the API running and CORS configured?";
            showSwal("Failure", msg, 400, undefined);
          }
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    }
  }, [user]);

  const handleEmailChange = (emailText) => {
    setForm((prevState) => ({ ...prevState, email: emailText }));
    emailValidation(emailText);
  };
  const handleForgotEmailChange = (emailText) => {
    setForm((prevState) => ({ ...prevState, forgotEmail: emailText }));
    emailValidation(emailText);
  };

  const emailValidation = (emailText) => {
    if (isValidEmail(emailText) === "Invalid Email") {
      setEmailError(true);
    } else {
      setEmailError(false);
    }
  };

  const handlePasswordChange = (passwordInput) => {
    setForm((prevState) => ({ ...prevState, password: passwordInput }));
  };
  const handleForgotPassword = () => {
    setShowForgotScreen(!showForgotScreen);
  };

  const [passwordVisible, setPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleUserDetails = async () => {
    if (form.email != "" && form.password != "") {
      setIsLoading(true);
      try {
        let response = await loginUser({
          email: form.email.toLowerCase(),
          password: form.password,
        });
        dispatch(loginSuccess(response?.data?.user, response?.data?.token));
        setIsLoading(false);
        // Show toast but navigate immediately for better UX
        showSwal("Success", response?.data?.message, 200);
        navigate(`/${response?.data?.user?.role === "superAdmin"
          ? "admin"
          : response.data.user.role
          }/${response.data.user.role === "admin" ? "users" : response.data.user.role === "superAdmin" ? "all-quotes" : response.data.user.role === "operator" ? "dashboard" : "freight-form"}`
        );
      } catch (error) {
        console.log(error);
        setIsLoading(false);
        const msg =
          error.response?.data?.error ||
          error.message ||
          "Network error — is the API running and CORS configured?";
        showSwal("Failure", msg, 400, undefined);
      }
    } else {
      showSwal("Please Fill in all the details", undefined, 400, undefined);
    }
  };

  const handleResetPassword = async () => {
    if (form.forgotEmail != "") {
      setIsLoading(true);
      try {
        let response = await forgotPassword({
          email: form.forgotEmail,
        });
        setIsLoading(false);
        showSwal("Check Your Email", response.data.message, 200);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
        showSwal(
          "Failure",
          error.response?.data?.error || error.message || "Request failed",
          400,
          undefined
        );
      }
    } else {
      showSwal("Please Check the email Address", undefined, 400, undefined);
    }
  };

  const handleButtonClick = BetterThrottle(handleUserDetails, 200, BtnRef);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative flex min-h-[calc(100vh-var(--fk-header-h))] flex-col bg-fk-canvas px-4 py-10 text-slate-900 dark:bg-[#0A0F1A] dark:text-slate-100"
      >
        {/* Subtle background wash */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.06),transparent_50%)]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.10),transparent_50%)] opacity-0 transition-opacity duration-700 dark:opacity-100"
        />
        {/* Aurora behind the sign-in cards. */}
        <AmbientBackdrop intensity={0.9} />
        {/* Same always-on freight wallpaper as the authed app (routes, drifting
            planes/trucks/boxes) — scoped absolute to this page, behind the cards. */}
        <FkAmbientCss className="absolute" intensity={0.9} />

        <div className="relative z-10 flex flex-1 items-center">
          <div className="mx-auto w-full max-w-[1060px]">
            {/* One unified card (Sign In design): dark flight-deck hero on the
                left, focused form on the right. */}
            <div className="grid overflow-hidden rounded-[26px] border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.06),0_30px_70px_-28px_rgba(15,23,42,0.35)] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] lg:grid-cols-[1.08fr_0.92fr]">

              {/* ── Left: flight-deck hero (decorative, lg+ only) ── */}
              <section
                className="relative hidden min-h-[612px] flex-col overflow-hidden p-9 text-white lg:flex"
                style={{ background: "radial-gradient(125% 120% at 12% 8%, #1b2450 0%, #0e1730 46%, #080d1d 100%)" }}
                aria-hidden="true"
              >
                {/* inner glows */}
                <div className="fkl-glow pointer-events-none absolute -left-10 -top-16 h-80 w-80 rounded-full opacity-30 blur-[46px]" style={{ background: "radial-gradient(circle,#3b6bff 0%,transparent 66%)" }} />
                <div className="fkl-glow pointer-events-none absolute -bottom-24 -right-12 h-80 w-80 rounded-full opacity-25 blur-[50px]" style={{ background: "radial-gradient(circle,#8b5cf6 0%,transparent 66%)", animationDelay: "1.5s" }} />
                {/* starfield */}
                {[["14%", "22%", 3, "0s"], ["30%", "68%", 2, "1s"], ["62%", "16%", 2, "2s"], ["78%", "80%", 3, ".5s"], ["47%", "90%", 2, "1.6s"], ["8%", "50%", 2, "2.4s"]].map(([t, l, s, d], i) => (
                  <span key={i} className="fkl-twinkle absolute rounded-full bg-white" style={{ top: t, left: l, width: s, height: s, animationDelay: d }} />
                ))}

                {/* Our logo — unchanged, just seated on the hero */}
                <div className="relative flex items-center gap-2.5">
                  <AnimatedLoader variant="route" size={44} label="FreightKit" extraProps={{ logo: true }} />
                  <span className="text-[19px] font-extrabold tracking-tight">FreightKit</span>
                </div>

                {/* headline */}
                <div className="relative mt-6">
                  <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-400/15 px-3 py-1.5 text-[11px] font-bold text-emerald-300">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-300" aria-hidden />
                    Systems operational
                  </div>
                  <h1 className="text-[42px] font-extrabold leading-[1.04] tracking-[-0.032em]">
                    Welcome{" "}
                    <span className="bg-gradient-to-r from-[#7aa2ff] to-[#c4b5fd] bg-clip-text text-transparent">back.</span>
                  </h1>
                  <p className="mt-3.5 max-w-[360px] text-[14.5px] leading-relaxed text-white/75">
                    Sign in to manage quotes with consistent margins, operator oversight, and templates you can adjust.
                  </p>
                </div>

                {/* centre: our animated globe with floating live cards */}
                <div className="relative my-2 min-h-0 flex-1">
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[47%]">
                    <div className="fkl-glow absolute inset-8 rounded-full blur-[10px]" style={{ background: "radial-gradient(circle at 42% 38%, rgba(90,140,255,0.5), transparent 68%)" }} />
                    <AnimatedLoader variant="globe" size={250} label="Global routes" extraProps={{ bold: true, hero: true }} />
                  </div>

                  <div className="fkl-float-a pointer-events-none absolute right-0 top-[6%] flex items-center gap-2.5 rounded-xl border border-white/15 bg-white/10 px-3 py-2 shadow-[0_14px_34px_-14px_rgba(0,0,0,0.6)]">
                    <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-lg bg-emerald-400/20 text-emerald-300"><Check size={14} /></span>
                    <span>
                      <span className="block text-[12.5px] font-semibold text-white" style={{ fontFamily: '"IBM Plex Mono", ui-monospace, monospace' }}>MAN → LOS</span>
                      <span className="mt-0.5 block text-[10.5px] text-white/60">Quote won · £8,420</span>
                    </span>
                  </div>
                  <div className="fkl-float-b pointer-events-none absolute -left-1.5 top-[44%] flex items-center gap-2.5 rounded-xl border border-white/15 bg-white/10 px-3 py-2 shadow-[0_14px_34px_-14px_rgba(0,0,0,0.6)]">
                    <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-lg bg-blue-400/20 text-[#8fb4ff]"><Plane size={14} /></span>
                    <span>
                      <span className="block text-[12px] font-bold text-white">Rate received</span>
                      <span className="mt-0.5 block text-[10.5px] text-white/60">Visual Air · 2.85 £/kg</span>
                    </span>
                  </div>
                  <div className="fkl-float-c pointer-events-none absolute bottom-[4%] right-[8%] flex items-center gap-2 rounded-[11px] border border-white/15 bg-white/10 px-3 py-2 shadow-[0_14px_34px_-14px_rgba(0,0,0,0.6)]">
                    <WandSparkles size={14} className="text-[#c4b5fd]" />
                    <span className="text-[11.5px] font-semibold text-white">Parsed 5 fields</span>
                  </div>
                </div>

                {/* bottom: live pipeline + capability chips */}
                <div className="relative flex flex-wrap gap-2">
                  <div className="relative flex items-center gap-2 overflow-hidden rounded-xl border border-white/[0.13] bg-white/[0.07] px-3.5 pb-2.5 pt-2 text-[12px] font-bold">
                    <span className="text-white/60">Inbox</span>
                    <ArrowRight size={12} className="text-white/40" />
                    <span className="text-white/60">Rates</span>
                    <ArrowRight size={12} className="text-white/40" />
                    <span className="text-white/60">Quote</span>
                    <span aria-hidden className="absolute inset-x-0 bottom-0 h-[2px] bg-white/10" />
                    <span aria-hidden className="absolute bottom-[-1px] h-[5px] w-5 rounded-full" style={{ background: "radial-gradient(circle,#7aa2ff,transparent 72%)", animation: "fkl-pipe 4.5s ease-in-out infinite" }} />
                  </div>
                  <div className="flex items-center gap-1.5 rounded-xl border border-white/[0.13] bg-white/[0.07] px-3 py-2 text-[12px] font-semibold text-white/70">
                    <FileCheck2 size={13} className="text-[#8fb4ff]" />
                    Customer-ready
                  </div>
                  <div className="flex items-center gap-1.5 rounded-xl border border-white/[0.13] bg-white/[0.07] px-3 py-2 text-[12px] font-semibold text-white/70">
                    <Shield size={13} className="text-emerald-300" />
                    Margin guardrails
                  </div>
                </div>
              </section>

              {/* ── Right: the real sign-in form ── */}
              <section className="relative flex flex-col justify-center bg-white/95 px-7 py-12 dark:bg-[oklch(0.225_0.026_264)] sm:px-11">
                {isLoading ? (
                  <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/85 dark:bg-[oklch(0.225_0.026_264/0.9)]">
                    <div className="flex flex-col items-center gap-1">
                      <AnimatedLoader variant="globe" size={130} label="Signing in" />
                      <div className="text-[13px] font-medium text-slate-600 dark:text-slate-300">Signing in…</div>
                    </div>
                  </div>
                ) : null}

                <div className="mx-auto w-full max-w-[376px]">
                  <div className="mb-3 text-[11px] font-semibold tracking-[0.16em] text-slate-400 dark:text-slate-500" style={{ fontFamily: '"IBM Plex Mono", ui-monospace, monospace' }}>
                    OPERATOR ACCESS
                  </div>
                  <h2 className="text-[30px] font-extrabold tracking-[-0.03em] text-slate-900 dark:text-white">
                    {showForgotScreen ? "Sign in" : "Reset password"}
                  </h2>
                  <p className="mb-7 mt-2 text-[14px] leading-relaxed text-slate-500 dark:text-slate-400">
                    {showForgotScreen
                      ? "Fast, consistent quoting. Review and adjust before anything is sent."
                      : "Enter your email and we’ll send you a reset link."}
                  </p>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (showForgotScreen) handleButtonClick();
                      else handleResetPassword();
                    }}
                    className="space-y-[18px]"
                  >
                    {showForgotScreen ? (
                      <>
                        <label className="block">
                          <div className="mb-2 text-[12.5px] font-bold text-slate-600 dark:text-slate-300">Email</div>
                          <div className="relative">
                            <Mail size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden />
                            <input
                              type="email"
                              placeholder="name@company.com"
                              autoComplete="username"
                              className="h-[50px] w-full rounded-xl border-[1.5px] border-slate-200 bg-white pl-[42px] pr-3.5 text-[14.5px] font-medium text-slate-900 outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:border-fk-accent focus:ring-4 focus:ring-fk-accent/10 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-500"
                              value={form.email}
                              onChange={(e) => handleEmailChange(e.target.value)}
                            />
                          </div>
                          {emailError ? <div className="mt-1.5 text-xs text-red-500">Invalid email</div> : null}
                        </label>

                        <label className="block">
                          <div className="mb-2 flex items-baseline justify-between">
                            <div className="text-[12.5px] font-bold text-slate-600 dark:text-slate-300">Password</div>
                            <button
                              type="button"
                              onClick={handleForgotPassword}
                              className="rounded-md px-1 text-[12.5px] font-semibold text-fk-accent hover:text-fk-accent/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fk-accent/25"
                            >
                              Forgot password?
                            </button>
                          </div>
                          <div className="relative">
                            <Lock size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden />
                            <input
                              type={passwordVisible ? "text" : "password"}
                              placeholder="Your password"
                              autoComplete="current-password"
                              className="h-[50px] w-full rounded-xl border-[1.5px] border-slate-200 bg-white pl-[42px] pr-11 text-[14.5px] font-medium text-slate-900 outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:border-fk-accent focus:ring-4 focus:ring-fk-accent/10 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-500"
                              value={form.password}
                              onChange={(e) => handlePasswordChange(e.target.value)}
                            />
                            <button
                              type="button"
                              onClick={togglePasswordVisibility}
                              className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200"
                              aria-label={passwordVisible ? "Hide password" : "Show password"}
                            >
                              {passwordVisible ? <EyeOff size={17} /> : <Eye size={17} />}
                            </button>
                          </div>
                        </label>

                        <button
                          type="submit"
                          disabled={isLoading}
                          className="group relative mt-1 flex h-[52px] w-full items-center justify-center gap-2.5 overflow-hidden rounded-[13px] bg-fk-accent text-[14.5px] font-bold text-white shadow-[0_12px_26px_-10px_rgba(37,99,235,0.8)] transition hover:-translate-y-px hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-70"
                        >
                          <span aria-hidden className="fkl-sheen-hover pointer-events-none absolute inset-y-0 left-0 w-[70px] bg-gradient-to-r from-transparent via-white/35 to-transparent" />
                          <span className="relative">Sign in</span>
                          <Send size={16} className="fkl-takeoff relative" aria-hidden />
                        </button>
                      </>
                    ) : (
                      <>
                        <label className="block">
                          <div className="mb-2 text-[12.5px] font-bold text-slate-600 dark:text-slate-300">Email</div>
                          <div className="relative">
                            <Mail size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden />
                            <input
                              type="email"
                              placeholder="name@company.com"
                              className="h-[50px] w-full rounded-xl border-[1.5px] border-slate-200 bg-white pl-[42px] pr-3.5 text-[14.5px] font-medium text-slate-900 outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:border-fk-accent focus:ring-4 focus:ring-fk-accent/10 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-500"
                              value={form.forgotEmail}
                              onChange={(e) => handleForgotEmailChange(e.target.value)}
                            />
                          </div>
                          {emailError ? <div className="mt-1.5 text-xs text-red-500">Invalid email</div> : null}
                        </label>

                        <button
                          type="submit"
                          disabled={isLoading}
                          className="group relative flex h-[52px] w-full items-center justify-center gap-2.5 overflow-hidden rounded-[13px] bg-fk-accent text-[14.5px] font-bold text-white shadow-[0_12px_26px_-10px_rgba(37,99,235,0.8)] transition hover:-translate-y-px hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-70"
                        >
                          <span aria-hidden className="fkl-sheen-hover pointer-events-none absolute inset-y-0 left-0 w-[70px] bg-gradient-to-r from-transparent via-white/35 to-transparent" />
                          <span className="relative">Send reset link</span>
                        </button>

                        <button
                          type="button"
                          onClick={handleForgotPassword}
                          className="w-full py-2 text-center text-[13px] font-medium text-slate-500 hover:text-slate-900 focus-visible:outline-none dark:text-slate-400 dark:hover:text-white"
                        >
                          Back to sign in
                        </button>
                      </>
                    )}
                  </form>

                  <div className="mt-6 flex items-center justify-center gap-2 text-[12.5px] text-slate-400 dark:text-slate-500">
                    <ShieldCheck size={14} className="text-emerald-500" aria-hidden />
                    Operator oversight on every quote sent
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default Login;
